import { Component } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UploadInvoiceService } from '../../service/upload-invoice-service';
import {PurchaseOrderService} from '../../service/vendor/purchase-order.service'
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { MessageService } from 'primeng/api';
import { Router } from '@angular/router';

@Component({
  selector: 'app-upload-invoice',
  templateUrl: './upload-invoice.component.html',
  styleUrl: './upload-invoice.component.scss'
})
export class UploadInvoiceComponent {
  invoiceForm: FormGroup;

  uploadedFiles: any[] = [];
  lineData: any[] = [];;

  loading: boolean = false;
  isViewInvoiceLineItems: boolean = false

  currentPdfUrl: string = '';
  selectedInvoiceNumber: string = '';
  selectedInvoicePO: string = '';

  FRAPPE_API_URL = environment.FRAPPE_API_URL

  constructor(
    private formBuilder: FormBuilder,
    private uploadInvoiceService: UploadInvoiceService,
    private messageService: MessageService,
    private purchaseOrderService: PurchaseOrderService,
    private router: Router,
  ) {
    this.invoiceForm = this.formBuilder.group({
      invoices: this.formBuilder.array([]),
    });
  }

  get invoices(): FormArray {
    return this.invoiceForm.get('invoices') as FormArray;
  }

  createInvoiceGroup(data: any): FormGroup {
    return this.formBuilder.group({
      invoice_number: [data.invoice_number],
      order_number: [data.order_number],
      invoice_date: [data.invoice_date],
      total_amount: [data.total_amount],
      invoice_language: [data.invoice_language],
      from_address: [data.from_address],
      to_address: [data.to_address],
      invoice: [data.invoice],
      line_items: this.formBuilder.array([])
    });
  }

  createLineItemGroup(lineItem: any): FormGroup {
    return this.formBuilder.group({
      quantity: [lineItem.quantity],
      description: [lineItem.description],
      list_price: [lineItem.list_price],
      net_price: [lineItem.net_price],
      amount: [lineItem.amount],
      part_number: [lineItem.part_number],
    });
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  onDrop(event: DragEvent) {
    event.preventDefault();

    if (event.dataTransfer && event.dataTransfer.files.length > 0) {
      for (let i = 0; i < event.dataTransfer.files.length; i++) {
        this.uploadedFiles.push(event.dataTransfer.files[i]);
      }
    }
  }

  onUpload(event: any) {
    this.uploadedFiles = event.files;
    this.setData();
  }

  onRemove(event: any) {
    const index = this.uploadedFiles.findIndex(file => file.name === event.file.name);
    if (index !== -1) {
      this.uploadedFiles.splice(index, 1);
    }
  }

  async onUplosdDocuments(file: File, fileUrl: string) {
    this.loading = true
    const invoiceFileUploadDocuments = {
      is_private: '1',
      folder: 'Home',
      file_url: fileUrl,
      doctype: 'Purchase Order',
      docname: file.name,
      fieldname: 'invoice',
    };
    const path = `${this.FRAPPE_API_URL}/api/method/upload_file`;
    try {
      const response = await firstValueFrom(
        this.uploadInvoiceService.uploadFile(path, file, invoiceFileUploadDocuments)
      );
      return response;
    } catch (error) {
      this.loading = false
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to upload invoice.' });
      throw error;
    }
  }

  async setData() {
    this.loading = true;
    this.invoices.clear();

    const invoicePromises = this.uploadedFiles.map(async (file) => {
      const fileUrl = `/private/files/${file.name}`;

      try {
        await this.onUplosdDocuments(file, fileUrl);
        const response = await firstValueFrom(this.uploadInvoiceService.parsePdfInvoice(file));
        const parsedData = this.parseApiResponse(response.message);
        console.log(parsedData)
        const data = {
          invoice_number: parsedData.invoice_number || '',
          order_number: parsedData.order_number || '',
          invoice_date: parsedData.invoice_date || '',
          total_amount: parsedData.total_amount || 0,
          invoice_language: parsedData.invoice_language || '',
          from_address: parsedData.from_address || '',
          to_address: parsedData.to_address || '',
          invoice: fileUrl,
        };
        this.currentPdfUrl = fileUrl;
        const invoiceGroup = this.createInvoiceGroup(data);
        if (parsedData.lines && parsedData.lines.length > 0) {
          this.addLineItems(invoiceGroup, parsedData.lines);
        }

        this.invoices.push(invoiceGroup);
      } catch (error) {
        console.error(error);
        this.loading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to get invoice details.' });
      }
    });

    await Promise.all(invoicePromises);
    this.loading = false;
  }

  private addLineItems(invoiceGroup: any, lines: any[]) {
    const lineItemsFormArray = invoiceGroup.get('line_items') as FormArray;

    lines.forEach((lineItem: any) => {

      const lineData = {
        quantity: lineItem.quantity || 0,
        description: lineItem.description || '',
        list_price: lineItem.list_price || 0,
        net_price: lineItem.net_price || 0,
        amount: lineItem.amount || 0,
        part_number: lineItem.part_number || '',
      };

      lineItemsFormArray.push(this.createLineItemGroup(lineData));
    });
  }

  parseApiResponse(response: any): any {
    const parsedData: any = {};

    response.forEach((item: any) => {
      const key = item[0];
      const value = item[1];

      if (key === 'line_items') {
        parsedData.lines = value.map((line: any) => {
          const lineItem: any = {};
          line.forEach((pair: any) => {
            const [key, val] = pair;
            lineItem[key] = val;
          });
          return lineItem;
        });
      } else {
        parsedData[key] = value;
      }
    });

    return parsedData;
  }


  viewPdf() {
    const baseUrl = environment.FRAPPE_API_URL;
    const fullPdfUrl = `${baseUrl}${this.currentPdfUrl}`;
    window.open(fullPdfUrl, '_blank');
  }

  viewInvoiceLineItems(invoice: any) {
    this.isViewInvoiceLineItems = true;
    this.selectedInvoiceNumber = invoice.invoice_number;
    this.selectedInvoicePO = invoice.order_number;
    this.lineData = invoice.line_items
  }
  onSubmit() {
    if (this.invoiceForm.valid) {
      this.loading=true
      this.purchaseOrderService.create_po_by_invoice(this.invoiceForm.value).then((res=>{
        if (res.message.status === "Success") {
          this.messageService.add({ severity: "success", summary: 'Success', detail: res.message.message })
          if(res.message.data !== "GRIR"){
            setTimeout(() => { this.router.navigate(['view-purchase-order']) }, 1500)
          }
        } else {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: res.message.message });
        }
        this.loading=false
      })).catch((error) => {
        console.log(error);
        this.loading=false
      })
    } else {
      this.messageService.add({ severity: 'warn', summary: 'Validation Error', detail: 'Please fill all values' });
    }
  }

}
